/**
 * Profile API Client
 *
 * Gestion du profil utilisateur (background, avatar, informations personnelles)
 */

import { fetchWithAuth } from '@/utils/api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

// Types pour les backgrounds
export type BackgroundType = 'PREDEFINED' | 'CUSTOM' | 'NONE';

export interface PredefinedBackground {
  id: string;
  name: string;
  type: 'gradient' | 'pattern' | 'solid';
  value: string;
  backgroundSize?: string;
  category: 'gradients' | 'patterns' | 'solids';
}

export interface BackgroundsResponse {
  backgrounds: Record<string, PredefinedBackground>;
  byCategory: {
    gradients: PredefinedBackground[];
    patterns: PredefinedBackground[];
    solids: PredefinedBackground[];
  };
  total: number;
}

export interface UpdateBackgroundPayload {
  backgroundType: BackgroundType;
  background?: string | null;
}

export interface BackgroundResult {
  backgroundType: BackgroundType;
  background: string | null;
}

export interface UploadBackgroundResult extends BackgroundResult {
  upload: {
    url: string;
    key: string;
    size: number;
    contentType: string;
  };
}

export interface UploadAvatarResult {
  avatar: string;
  upload: {
    url: string;
    key: string;
    size: number;
    contentType: string;
  };
}

export interface UserProfile {
  id: string;
  email: string;
  fullName: string | null;
  phone: string | null;
  avatar: string | null;
  username: string | null;
  locale: string | null;
  timezone: string | null;
  backgroundType: BackgroundType;
  background: string | null;
}

const API_BASE = `${API_URL}/core/auth`;

export const profileApi = {
  /**
   * Récupérer le profil de l'utilisateur connecté
   */
  async getMe(): Promise<UserProfile> {
    const response = await fetchWithAuth(`${API_BASE}/me`, {});
    const result = await response.json();
    return result.data;
  },

  /**
   * Récupérer la liste des backgrounds prédéfinis
   */
  async getAvailableBackgrounds(): Promise<BackgroundsResponse> {
    const response = await fetchWithAuth(`${API_BASE}/backgrounds`, {});
    const result = await response.json();
    return result.data;
  },

  /**
   * Mettre à jour le background de l'utilisateur
   */
  async updateBackground(payload: UpdateBackgroundPayload): Promise<BackgroundResult> {
    const response = await fetchWithAuth(`${API_BASE}/profile/background`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    const result = await response.json();
    return result.data;
  },

  /**
   * Uploader un background personnalisé
   */
  async uploadBackground(file: File): Promise<UploadBackgroundResult> {
    const formData = new FormData();
    formData.append('background', file);

    const response = await fetchWithAuth(`${API_BASE}/profile/background/upload`, {
      method: 'POST',
      body: formData,
    });
    const result = await response.json();
    return result.data;
  },

  /**
   * Supprimer le background et revenir au défaut
   */
  async deleteBackground(): Promise<BackgroundResult> {
    const response = await fetchWithAuth(`${API_BASE}/profile/background`, {
      method: 'DELETE',
    });
    const result = await response.json();
    return result.data;
  },

  /**
   * Uploader un avatar personnalisé
   */
  async uploadAvatar(file: File): Promise<UploadAvatarResult> {
    const formData = new FormData();
    formData.append('avatar', file);

    const response = await fetchWithAuth(`${API_BASE}/profile/avatar/upload`, {
      method: 'POST',
      body: formData,
    });
    const result = await response.json();
    return result.data;
  },

  /**
   * Mettre à jour le profil utilisateur
   */
  async updateProfile(data: Partial<Pick<UserProfile, 'fullName' | 'phone' | 'locale' | 'timezone' | 'username'>>): Promise<UserProfile> {
    const response = await fetchWithAuth(`${API_BASE}/profile`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    const result = await response.json();
    return result.data;
  },
};

export default profileApi;
